// The catnip trip, beat by beat, for every cat that catches the leaf. Pure: no three.js, no DOM.
//
// On the site a cat that reaches the catnip freezes for exactly 10 s, then reappears at a random
// point in its own lane (landscape/kissaderby-race.md). We stage those 10 s in four beats
// (ux/index.md, ui/index.md):
//
//   catch    0–2 s   bites the leaf and stops; eyes go blank; the first ripple in its lane
//   lift     2–4 s   curls up and floats into the air; its lane becomes a ribbon of light
//   trip     4 s …   drifts in the psychedelic sky, anchored over the spot where it caught the leaf
//   descent  1.5 s   timed backwards from the landing frame, so the cat touches down exactly when
//                    the signal moves it: feet first, onto the reported spot, in its own lane
//   landed   0.7 s   stagger while running on from where the signal has it, then hand back
//   drain    2.5 s   the lane colour drains away; the cat is already running normally
//
// The landing spot is known about 2 s before it is shown (the playout delay; RaceTimeline.peek),
// so the descent aims at the true spot and never guesses. If the landing has not arrived when the
// descent would start, the cat keeps floating until it does.
import { laneCenterZ, posToX } from '../../data/track';
import type { CatId, CatSnapshot, RaceEvent } from '../../race/types';

export const CATCH_MS = 2000;
export const LIFT_MS = 2000;
export const DESCENT_MS = 1500;
export const LANDED_MS = 700;
export const DRAIN_MS = 2500;
/** How high the cat floats, metres. */
export const FLOAT_HEIGHT = 2.4;

export type TripBeat = 'catch' | 'lift' | 'trip' | 'descent' | 'landed' | 'drain';

export interface TripPose {
  x: number;
  y: number;
  z: number;
  /** Radians from +X towards +Z (0 = running down the track). */
  heading: number;
  roll: number;
  pitch: number;
  spin: number;
  mode: 'float' | 'land' | 'run';
  /** 0..1, the blank glowing eyes. */
  tripEyes: number;
}

export interface TripLanding {
  t: number;
  from: number;
  to: number;
  delta: number;
}

export interface TripFrame {
  cat: CatId;
  lane: number;
  beat: TripBeat;
  /** 0..1 through the current beat (the trip beat counts towards the expected 8.5 s). */
  beatT: number;
  /** ms since the catch. */
  elapsed: number;
  /** Where the cat is and how it is posed; null once it has been handed back to normal running. */
  pose: TripPose | null;
  /** 0..1 strengths for the lane swirl, the trip sky and the screen effect. */
  laneGlow: number;
  sky: number;
  screen: number;
  /** The landing, once the signal has reported it (possibly still in the future). */
  landing: TripLanding | null;
  /** True on the frame the cat touches down. */
  touchdown: boolean;
  /** This catch came straight after a landing. */
  again: boolean;
}

interface Trip {
  cat: CatId;
  lane: number;
  startT: number;
  at: number;
  landing: TripLanding | null;
  touchedDown: boolean;
  again: boolean;
  /** No landing will come (the race ended or the cat stopped tripping without one). */
  abandonedAt: number | null;
}

const ease = (k: number) => { const c = Math.min(1, Math.max(0, k)); return c * c * (3 - 2 * c); };
const clamp01 = (k: number) => Math.min(1, Math.max(0, k));
const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

/** The pose while catching, lifting and floating, `e` ms after the catch. */
export function floatPose(e: number, x: number, z: number): TripPose {
  if (e < CATCH_MS) {
    return { x, y: 0, z, heading: 0, roll: 0, pitch: 0, spin: 0, mode: 'run', tripEyes: clamp01(e / 600) };
  }
  const lift = ease((e - CATCH_MS) / LIFT_MS);
  const s = (e - CATCH_MS) / 1000;
  const drift = Math.max(0, e - CATCH_MS - LIFT_MS) / 1000;
  return {
    x,
    y: FLOAT_HEIGHT * lift + Math.sin(drift * 1.3) * 0.22 * lift,
    z,
    heading: 0,
    roll: Math.sin(s * 0.9) * 0.45 * lift,
    pitch: Math.sin(s * 0.7 + 1) * 0.3 * lift,
    spin: s * s * 0.25 * lift + drift * 0.5,
    mode: 'float',
    tripEyes: 1,
  };
}

export class TripSequencer {
  private trips = new Map<CatId, Trip>();

  /** Active trips, for tests and diagnostics. */
  get active(): number { return this.trips.size; }

  /**
   * Advance to render time `t` (signal clock, ms). `events` fired since the last frame; `peek` is
   * the look-ahead (RaceTimeline.peek), where the landing first shows up.
   */
  update(t: number, cats: readonly CatSnapshot[], events: readonly RaceEvent[], peek: readonly RaceEvent[]): TripFrame[] {
    for (const e of events) {
      if (e.type !== 'catnip-catch') continue;
      const c = cats.find((x) => x.id === e.cat);
      const prev = this.trips.get(e.cat);
      this.trips.set(e.cat, {
        cat: e.cat, lane: c?.lane ?? 0, startT: e.t, at: e.at, landing: null, touchedDown: false,
        again: !!prev && prev.touchedDown, abandonedAt: null,
      });
    }
    for (const e of [...events, ...peek]) {
      if (e.type !== 'catnip-landing') continue;
      const trip = this.trips.get(e.cat);
      if (trip && !trip.landing && e.t > trip.startT) trip.landing = { t: e.t, from: e.from, to: e.to, delta: e.delta };
    }
    // Joined mid-trip (no catch seen): pick it up in the floating beat over its frozen spot.
    for (const c of cats) {
      if (c.tripping && !this.trips.has(c.id)) {
        this.trips.set(c.id, { cat: c.id, lane: c.lane, startT: t - CATCH_MS - LIFT_MS, at: c.signalPos, landing: null, touchedDown: false, again: false, abandonedAt: null });
      }
    }

    const out: TripFrame[] = [];
    for (const [id, trip] of this.trips) {
      const c = cats.find((x) => x.id === id);
      // Stopped tripping with no landing reported (a new race, a jump across a gap): wind down.
      if (!trip.landing && c && !c.tripping && t > trip.startT + 300 && trip.abandonedAt === null) trip.abandonedAt = t;
      const f = this.frame(trip, t, c);
      if (!f) { this.trips.delete(id); continue; }
      out.push(f);
    }
    return out;
  }

  private frame(trip: Trip, t: number, c: CatSnapshot | undefined): TripFrame | null {
    const e = t - trip.startT;
    const x0 = posToX(trip.at);
    const z = laneCenterZ(trip.lane);
    const base = { cat: trip.cat, lane: trip.lane, elapsed: e, landing: trip.landing, again: trip.again, touchdown: false };

    if (trip.abandonedAt !== null) {
      const k = (t - trip.abandonedAt) / DRAIN_MS;
      if (k >= 1) return null;
      return { ...base, beat: 'drain', beatT: k, pose: null, laneGlow: 1 - k, sky: 0, screen: 0 };
    }

    const L = trip.landing;
    if (L && t >= L.t) {
      const since = t - L.t;
      const touchdown = !trip.touchedDown;
      trip.touchedDown = true;
      if (since < LANDED_MS) {
        const x = c ? posToX(c.displayPos) : posToX(L.to);
        const k = since / LANDED_MS;
        return {
          ...base, touchdown, beat: 'landed', beatT: k,
          pose: { x, y: 0, z, heading: 0, roll: 0, pitch: 0, spin: 0, mode: 'land', tripEyes: 1 - k },
          laneGlow: 1, sky: 0.15 * (1 - k), screen: 0.35 * (1 - ease(since / 800)),
        };
      }
      const k = (since - LANDED_MS) / DRAIN_MS;
      if (k >= 1) return null;
      return { ...base, touchdown, beat: 'drain', beatT: k, pose: null, laneGlow: 1 - ease(k), sky: 0, screen: 0 };
    }

    // descent: aimed at the reported spot, timed so touchdown is the landing frame
    if (L) {
      const dStart = Math.max(L.t - DESCENT_MS, trip.startT + 600);
      if (t >= dStart) {
        const span = Math.max(1, L.t - dStart);
        const k = clamp01((t - dStart) / span);
        const from = floatPose(dStart - trip.startT, x0, z);
        const xTo = posToX(L.to);
        const ek = ease(k);
        // falls faster at the end; a little hang at the top of the arc for a long move
        const travel = Math.abs(xTo - from.x);
        const hang = Math.sin(Math.PI * k) * Math.min(3, travel * 0.04);
        return {
          ...base, beat: 'descent', beatT: k,
          pose: {
            x: from.x + (xTo - from.x) * ek,
            y: Math.max(0, from.y * (1 - k * k) + hang * (1 - k)),
            z,
            heading: 0,
            roll: from.roll * (1 - ek),
            pitch: from.pitch * (1 - ek),
            spin: wrapAngle(from.spin) * (1 - ek),
            mode: k > 0.72 ? 'land' : 'float',
            tripEyes: 1,
          },
          laneGlow: 1, sky: 0.9 - 0.75 * ek, screen: 1 - 0.65 * ek,
        };
      }
    }

    const pose = floatPose(e, x0, z);
    if (e < CATCH_MS) {
      const k = e / CATCH_MS;
      return { ...base, beat: 'catch', beatT: k, pose, laneGlow: 0.45 * ease(k), sky: 0, screen: 0.25 * ease(k) };
    }
    if (e < CATCH_MS + LIFT_MS) {
      const k = (e - CATCH_MS) / LIFT_MS;
      return { ...base, beat: 'lift', beatT: k, pose, laneGlow: 0.45 + 0.55 * ease(k), sky: 0.65 * ease(k), screen: 0.25 + 0.5 * ease(k) };
    }
    const k = clamp01((e - CATCH_MS - LIFT_MS) / 4500);
    return { ...base, beat: 'trip', beatT: k, pose, laneGlow: 1, sky: 0.65 + 0.3 * ease(k * 3), screen: 0.75 + 0.25 * ease(k * 3) };
  }
}
